import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  Wind,
  Bug,
  CalendarCheck,
  Phone,
  MessageSquare,
  MapPin,
  ShieldCheck,
  Leaf,
  ThumbsUp,
  Plus,
  Minus,
  Clock,
  CheckCircle2,
  Loader2,
  ChevronDown,
  Images,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Star,
  X,
} from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HERO, FEATURES, BOOKING, ABOUT, FOOTER, NAV } from "@/constants/testIds";
import {
  getCustomerToken,
  customerAuthHeaders,
  lookupCustomer,
  registerCustomer,
  loginCustomer,
  logoutCustomer,
  fetchMe,
} from "@/lib/customerAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DANIEL_IMG = "/daniel_character.jpg";
const DANIEL_REAL_IMG =
  "https://customer-assets.emergentagent.com/job_daniel-cleanbin/artifacts/1mnuhlzj_image.png";

const CITIES = ["Wethersfield", "Rocky Hill", "Newington", "Berlin"];
const OUT_OF_AREA = "__other__";
const OUT_OF_AREA_SURCHARGE = 5;
const PHONE = "860-250-5755";
const PHONE_TEL = "+18602505755";
const PHONE_SMS = "18602505755";

function formatDateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatTime12h(hhmm) {
  const [hStr, mStr] = hhmm.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr;
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

function scrollToBooking() {
  const el = document.getElementById("booking");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function NavBar() {
  return (
    <nav className="sticky top-0 z-40 bg-[#FDFBF7]/95 backdrop-blur border-b-4 border-[#0B132B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <a href="/" data-testid={NAV.logo} className="flex items-center gap-2 self-start">
          <span className="inline-block w-9 h-9 rounded-full bg-[#00E676] border-[3px] border-[#0B132B] grid place-items-center">
            <Sparkles className="w-4 h-4 text-[#0B132B]" />
          </span>
          <span className="font-['Bebas_Neue'] text-2xl sm:text-3xl tracking-wider">
            <span className="text-[#00E676]">DANIEL'S</span>{" "}
            <span className="text-[#0B132B]">DIRTY DEEDS</span>
          </span>
        </a>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Need Something Else? button disabled per request — re-enable when ready
          <a
            href="#other-services"
            data-testid="nav-other-services-link"
            className="inline-flex text-xs sm:text-sm whitespace-nowrap"
            style={{
              padding: "0.45rem 0.75rem",
              background: "#FF3D00",
              color: "#FFFFFF",
              border: "3px solid #0B132B",
              borderRadius: "10px",
              boxShadow: "3px 3px 0 #0B132B",
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            Need Something Else?
          </a>
          */}
          <a
            href="#reviews"
            data-testid="nav-reviews-link"
            className="inline-flex text-xs sm:text-sm whitespace-nowrap"
            style={{
              padding: "0.45rem 0.75rem",
              background: "#FFC107",
              color: "#0B132B",
              border: "3px solid #0B132B",
              borderRadius: "10px",
              boxShadow: "3px 3px 0 #0B132B",
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            Reviews
          </a>
          <a
            href="/account"
            className="inline-flex text-xs sm:text-sm whitespace-nowrap"
            style={{
              padding: "0.45rem 0.75rem",
              background: "#FFFFFF",
              color: "#0B132B",
              border: "3px solid #0B132B",
              borderRadius: "10px",
              boxShadow: "3px 3px 0 #0B132B",
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
            data-testid="nav-account-link"
          >
            My Account
          </a>
          <button
            data-testid={NAV.bookNowBtn}
            onClick={scrollToBooking}
            className="comic-btn comic-btn-action text-xs sm:text-base"
            style={{ padding: "0.55rem 0.85rem", boxShadow: "3px 3px 0 #0B132B" }}
          >
            Book Now
          </button>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <section
      data-testid={HERO.section}
      className="relative overflow-hidden border-b-4 border-[#0B132B]"
    >
      <div className="absolute inset-0 halftone-blue" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7 text-white">
          <div className="inline-block tape px-4 py-1 rotate-n2 mb-5">
            <span className="font-['Bebas_Neue'] text-lg text-[#0B132B]">
              Wethersfield • Rocky Hill • Newington • Berlin
            </span>
          </div>
          <h1
            data-testid={HERO.heading}
            className="font-['Bebas_Neue'] leading-[0.9] tracking-tight uppercase"
            style={{ fontSize: "clamp(2.6rem, 7vw, 5.5rem)" }}
          >
            <span className="block text-[#00E676] drop-shadow-[4px_4px_0_#0B132B]">
              Daniel's
            </span>
            <span className="block drop-shadow-[4px_4px_0_#0B132B]">
              Dirty Deeds
            </span>
            <span className="block accent-script text-[#FFC107] mt-2 rotate-n2 text-3xl sm:text-5xl drop-shadow-[2px_2px_0_#0B132B]">
              trash bin cleaning service
            </span>
          </h1>

          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed bg-[#0B132B]/70 border-[3px] border-white/30 rounded-xl p-4">
            Your trash left… <span className="text-[#00E676] font-bold">the smell stayed.</span>{" "}
            Book a deep-clean for your bins and we come to you. Sanitized, deodorized,
            eco-friendly — and run by a local 14-year-old entrepreneur.
          </p>

          <div className="mt-7 flex flex-wrap gap-3 items-center">
            <button
              data-testid={HERO.ctaBook}
              onClick={scrollToBooking}
              className="comic-btn comic-btn-primary"
            >
              <CalendarCheck className="w-5 h-5" /> Schedule a Cleaning
            </button>
            <button
              data-testid={HERO.ctaCall}
              type="button"
              onClick={() => setPickerOpen(true)}
              className="comic-btn comic-btn-outline"
            >
              <Phone className="w-5 h-5" /> Call or Text Dan
            </button>
          </div>

          <div className="mt-6 flex flex-wrap gap-4 text-sm font-semibold text-white/90">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#00E676]" /> Sanitized & Disinfected
            </span>
            <span className="inline-flex items-center gap-2">
              <Leaf className="w-4 h-4 text-[#00E676]" /> Eco-Friendly Products
            </span>
            <span className="inline-flex items-center gap-2">
              <ThumbsUp className="w-4 h-4 text-[#00E676]" /> 100% Satisfaction
            </span>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative inline-block">
            <div className="comic-card-static p-3 bg-white rotate-p2 max-w-md">
              <img
                src={DANIEL_IMG}
                alt="Daniel, founder of Daniel's Dirty Deeds, ready to clean trash bins"
                className="block w-full h-auto rounded-lg"
              />
            </div>
            <div className="absolute -bottom-6 -right-2 sm:-right-8 bg-[#FFC107] border-[3px] border-[#0B132B] rounded-2xl px-5 py-3 rotate-p3 shadow-[5px_5px_0_#0B132B]">
              <div className="font-['Bebas_Neue'] text-3xl sm:text-4xl text-[#0B132B] leading-none">
                $15 <span className="text-lg">first bin</span>
              </div>
              <div className="font-['Bebas_Neue'] text-lg text-[#0B132B] leading-none mt-0.5">
                +$10 each extra
              </div>
              <div className="accent-script text-[#0B132B] text-base leading-none mt-1">
                clean bins. fresh start.
              </div>
            </div>
          </div>
        </div>
      </div>
      <ContactPicker open={pickerOpen} onOpenChange={setPickerOpen} />
    </section>
  );
}

const FEATURE_ITEMS = [
  {
    key: "deep-clean",
    icon: Sparkles,
    title: "Deep Cleans",
    desc: "Grime, gunk & built-up bacteria — gone. We pressure wash inside and out.",
    color: "#00E676",
  },
  {
    key: "odors",
    icon: Wind,
    title: "Eliminates Odors",
    desc: "Leaves bins fresh, clean, and smelling good — like a new bin again.",
    color: "#00E5FF",
  },
  {
    key: "pests",
    icon: Bug,
    title: "Keeps Pests Away",
    desc: "Reduces flies, maggots, raccoons & rodents that swarm dirty barrels.",
    color: "#FFC107",
  },
  {
    key: "curbside",
    icon: CalendarCheck,
    title: "Curbside Service",
    desc: "We come to you on trash day. No bin handling, no mess, no hassle.",
    color: "#FF3D00",
  },
];

function Features() {
  return (
    <section
      data-testid={FEATURES.section}
      className="relative py-16 sm:py-20 border-b-4 border-[#0B132B] bg-[#FDFBF7]"
    >
      <div className="absolute inset-0 halftone opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="inline-block tape px-4 py-1 rotate-p2 mb-4">
            <span className="font-['Bebas_Neue'] text-lg text-[#0B132B]">
              Why Clean Your Bins?
            </span>
          </div>
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl tracking-tight">
            We Do The <span className="text-[#1E88E5]">Dirty Deeds</span>, You Relax.
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURE_ITEMS.map(({ key, icon: Icon, title, desc, color }, i) => (
            <div
              key={key}
              data-testid={FEATURES.card(key)}
              className="comic-card p-5"
              style={{ transform: `rotate(${i % 2 === 0 ? "-1deg" : "1deg"})` }}
            >
              <div
                className="w-14 h-14 rounded-full border-[3px] border-[#0B132B] grid place-items-center mb-4"
                style={{ background: color }}
              >
                <Icon className="w-7 h-7 text-[#0B132B]" />
              </div>
              <h3 className="font-['Bebas_Neue'] text-2xl uppercase tracking-wide">
                {title}
              </h3>
              <p className="mt-2 text-[#0B132B]/80 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutDaniel() {
  return (
    <section
      data-testid={ABOUT.section}
      className="relative py-16 sm:py-20 border-b-4 border-[#0B132B] bg-[#1E88E5]"
    >
      <div className="absolute inset-0 halftone-blue opacity-60 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-10 items-center">
        <div className="comic-card-static p-3 bg-white rotate-n2 max-w-sm mx-auto md:mx-0">
          <img
            src={DANIEL_REAL_IMG}
            alt="Daniel — local Wethersfield teen entrepreneur"
            className="w-full h-auto rounded-lg"
          />
        </div>
        <div className="text-white">
          <div className="inline-block tape px-3 py-1 rotate-n2 mb-4">
            <span className="font-['Bebas_Neue'] text-lg text-[#0B132B]">
              About Daniel
            </span>
          </div>
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl">
            Meet the kid behind the hose.
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed">
            Daniel is a local Wethersfield teen who will be starting at{" "}
            <span className="text-[#00E676] font-bold">Wethersfield High School</span>{" "}
            this fall. He's spending his summer building a real business, learning to
            work hard, and serving the community he grew up in.
          </p>
          <ul className="mt-5 space-y-2 text-base">
            <li className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 bg-[#00E676] border-2 border-[#0B132B] rounded-sm rotate-12" />
              Loves football — go Eagles.
            </li>
            <li className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 bg-[#00E676] border-2 border-[#0B132B] rounded-sm rotate-12" />
              Believes in hard work.
            </li>
            <li className="flex items-center gap-3">
              <span className="w-2.5 h-2.5 bg-[#00E676] border-2 border-[#0B132B] rounded-sm rotate-12" />
              Proud to serve his community.
            </li>
          </ul>
          <p className="mt-5 accent-script text-2xl text-[#FFC107]">
            "Thank you for supporting a local young entrepreneur."
          </p>
        </div>
      </div>
    </section>
  );
}

function BinStepper({ value, setValue, firstBinPrice = 15, extraBinPrice = 10 }) {
  const dec = () => setValue(Math.max(1, value - 1));
  const inc = () => setValue(Math.min(10, value + 1));
  const total =
    value <= 1 ? firstBinPrice : firstBinPrice + (value - 1) * extraBinPrice;
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <button
        type="button"
        data-testid={BOOKING.binsDecrement}
        onClick={dec}
        className="comic-btn comic-btn-outline"
        style={{ padding: "0.5rem 0.8rem", fontSize: "1rem" }}
        aria-label="Decrease bins"
      >
        <Minus className="w-4 h-4" />
      </button>
      <div
        data-testid={BOOKING.binsValue}
        className="font-['Bebas_Neue'] text-3xl w-14 text-center border-[3px] border-[#0B132B] rounded-lg bg-white shadow-[3px_3px_0_#0B132B] py-1"
      >
        {value}
      </div>
      <button
        type="button"
        data-testid={BOOKING.binsIncrement}
        onClick={inc}
        className="comic-btn comic-btn-outline"
        style={{ padding: "0.5rem 0.8rem", fontSize: "1rem" }}
        aria-label="Increase bins"
      >
        <Plus className="w-4 h-4" />
      </button>
      <div className="text-sm text-[#0B132B]/70" data-testid="bins-price-breakdown">
        {value <= 1 ? (
          <>
            <span className="font-semibold">${firstBinPrice}</span> first bin ={" "}
            <span className="font-bold text-[#1E88E5]">${total.toFixed(2)}</span>
          </>
        ) : (
          <>
            <span className="font-semibold">${firstBinPrice}</span> + ${extraBinPrice} ×{" "}
            {value - 1} ={" "}
            <span className="font-bold text-[#1E88E5]">${total.toFixed(2)}</span>
          </>
        )}
      </div>
    </div>
  );
}

function BookingSection() {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [bins, setBins] = useState(1);
  const [city, setCity] = useState(CITIES[0]);
  const [outOfAreaCity, setOutOfAreaCity] = useState("");
  const isOutOfArea = city === OUT_OF_AREA;
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentMethod] = useState("cash");
  const [recurrence, setRecurrence] = useState("none");
  const [occurrences, setOccurrences] = useState(4);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  // Customer auth
  const [authed, setAuthed] = useState(!!getCustomerToken());
  const [me, setMe] = useState(null);
  const [emailStatus, setEmailStatus] = useState("idle"); // idle | checking | new | existing
  const [password, setPassword] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);

  // Pre-fill from logged-in customer
  useEffect(() => {
    if (!authed) return;
    fetchMe()
      .then((m) => {
        setMe(m);
        setName(m.name || "");
        setEmail(m.email || "");
        if (m.phone) setPhone(m.phone);
        setEmailStatus("existing");
      })
      .catch(() => {
        logoutCustomer();
        setAuthed(false);
      });
  }, [authed]);

  // Lookup customer only AFTER they finish typing the email (onBlur or initial
  // mount with a pre-filled value). Avoids mounting/unmounting the password
  // callout in step 6 on every keystroke, which was causing big layout jumps
  // on mobile during checkout.
  const runEmailLookup = async (raw) => {
    if (authed) return;
    const trimmed = (raw ?? email).trim();
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setEmailStatus("idle");
      return;
    }
    setEmailStatus("checking");
    try {
      const r = await lookupCustomer(trimmed);
      setEmailStatus(r.exists ? "existing" : "new");
    } catch {
      setEmailStatus("idle");
    }
  };

  // If a logged-in customer's email pre-fills the field, do the lookup once.
  useEffect(() => {
    if (authed) return;
    const t = email.trim();
    if (t && /^\S+@\S+\.\S+$/.test(t)) {
      // Defer so initial paint settles before the callout mounts.
      const id = setTimeout(() => runEmailLookup(t), 200);
      return () => clearTimeout(id);
    } else {
      setEmailStatus("idle");
    }
    // Only re-run when authed flips or email is wiped — NOT on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const blockMinutes = 120 + (bins - 1) * 15;
  const occCount = recurrence === "none" ? 1 : occurrences;

  // Dynamic pricing — re-fetched whenever bins / promo change.
  const [firstBinPrice, setFirstBinPrice] = useState(15);
  const [extraBinPrice, setExtraBinPrice] = useState(10);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, total, discount } | null
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState("");

  useEffect(() => {
    const params = { num_bins: bins };
    if (appliedPromo?.code) params.promo_code = appliedPromo.code;
    axios
      .get(`${API}/pricing`, { params })
      .then((res) => {
        const d = res.data || {};
        setFirstBinPrice(Number(d.first_bin_price ?? 15));
        setExtraBinPrice(Number(d.additional_bin_price ?? 10));
        // If a promo was attached but server now says it's invalid, drop it.
        if (appliedPromo?.code && !d.promo_applied) {
          setAppliedPromo(null);
          setPromoError("Promo code is no longer valid.");
        }
      })
      .catch(() => {
        setFirstBinPrice(15);
        setExtraBinPrice(10);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bins, appliedPromo?.code]);

  const applyPromoCode = async () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setPromoBusy(true);
    setPromoError("");
    try {
      const r = await axios.post(`${API}/promo-codes/validate`, {
        code,
        num_bins: bins,
      });
      setAppliedPromo({
        code: r.data.code,
        description: r.data.description,
        total: r.data.total,
        discount: r.data.discount,
      });
      toast.success(`Promo applied — saved $${r.data.discount.toFixed(2)}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setPromoError(typeof d === "string" ? d : "Invalid promo code");
      setAppliedPromo(null);
    } finally {
      setPromoBusy(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoCode("");
    setPromoError("");
  };

  const perVisitBase =
    bins <= 1 ? firstBinPrice : firstBinPrice + (bins - 1) * extraBinPrice;
  const surcharge = isOutOfArea ? OUT_OF_AREA_SURCHARGE : 0;
  const perVisit = perVisitBase + surcharge;
  const totalAmount = (perVisit * occCount).toFixed(2);

  const doInlineLogin = async () => {
    if (!email || !loginPassword) return;
    setAuthBusy(true);
    try {
      const r = await loginCustomer({ email: email.trim(), password: loginPassword });
      setMe(r.customer);
      setName(r.customer.name || name);
      if (r.customer.phone) setPhone(r.customer.phone);
      setAuthed(true);
      setLoginPassword("");
      toast.success("Welcome back!");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setAuthBusy(false);
    }
  };

  useEffect(() => {
    if (!date) {
      setSlots([]);
      setSelectedTime(null);
      return;
    }
    setLoadingSlots(true);
    setSelectedTime(null);
    const dStr = formatDateLocal(date);
    axios
      .get(`${API}/availability`, { params: { date_str: dStr, num_bins: bins } })
      .then((res) => {
        setSlots(res.data?.slots || []);
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== "production") console.error(err);
        toast.error("Couldn't load available times. Try again.");
        setSlots([]);
      })
      .finally(() => setLoadingSlots(false));
  }, [date, bins]);

  // Fetch fully-unavailable days so the calendar can disable them up-front.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/calendar/unavailable-dates`, { params: { days: 90 } })
      .then((res) => {
        if (cancelled) return;
        const list = (res.data?.unavailable_dates || []).map((iso) => {
          const [y, m, d] = iso.split("-").map((v) => parseInt(v, 10));
          return new Date(y, m - 1, d);
        });
        setUnavailableDates(list);
      })
      .catch(() => {
        if (!cancelled) setUnavailableDates([]);
      });
    return () => {
      cancelled = true;
    };
    // Re-pull when bins changes (slot occupancy can flip a day full/open).
  }, [bins]);

  // Stable refs for DayPicker so it doesn't re-render on every keystroke.
  const calendarFromDate = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const calendarDisabled = useMemo(
    () => [{ before: calendarFromDate }, ...unavailableDates],
    [calendarFromDate, unavailableDates],
  );

  const canSubmit =
    !!date &&
    !!selectedTime &&
    name.trim().length >= 2 &&
    /^\S+@\S+\.\S+$/.test(email) &&
    phone.replace(/\D/g, "").length >= 7 &&
    address.trim().length >= 3 &&
    (CITIES.includes(city) || (isOutOfArea && outOfAreaCity.trim().length >= 2)) &&
    !submitting &&
    // If new account, require a password. If existing not-logged-in, require inline login first.
    (authed
      ? true
      : emailStatus === "new"
        ? password.length >= 6
        : emailStatus === "existing"
          ? false
          : false);

  const onSubmit = async (e) => {
    e.preventDefault();
    // If user typed an email but never blurred the field, the lookup hasn't
    // fired yet — run it now and stop so the password callout can show.
    if (
      !authed &&
      emailStatus === "idle" &&
      /^\S+@\S+\.\S+$/.test(email.trim())
    ) {
      await runEmailLookup(email);
      setTimeout(() => {
        const el =
          document.querySelector('[data-testid="booking-new-password"]') ||
          document.querySelector('[data-testid="booking-login-password"]');
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => el.focus(), 350);
        }
      }, 50);
      return;
    }
    if (!canSubmit) {
      // Give a more targeted hint when the only thing missing is the password / login.
      if (!authed && emailStatus === "new" && password.length < 6) {
        toast.error("Please create a password to continue (at least 6 characters).");
        // Scroll the password field into view + focus it
        const el = document.querySelector('[data-testid="booking-new-password"]');
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => el.focus(), 350);
        }
        return;
      }
      if (!authed && emailStatus === "existing") {
        toast.error("Please log in with your password to continue.");
        const el = document.querySelector('[data-testid="booking-login-password"]');
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => el.focus(), 350);
        }
        return;
      }
      toast.error("Please fill out all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const [hh, mm] = selectedTime.split(":").map((v) => parseInt(v, 10));
      const local = new Date(date);
      local.setHours(hh, mm, 0, 0);
      // Send as naive ISO (no Z) — backend treats as local wall time
      const isoLocal = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(local.getDate()).padStart(2, "0")}T${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;

      const payload = {
        customer_name: name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim(),
        address: address.trim(),
        city: isOutOfArea ? outOfAreaCity.trim() : city,
        num_bins: bins,
        notes: notes.trim() || null,
        start_at: isoLocal,
        payment_method: paymentMethod,
        recurrence,
        occurrences: occCount,
        promo_code: appliedPromo?.code || null,
        out_of_area: isOutOfArea,
      };

      // If new customer and not yet authed, register them first.
      if (!authed && emailStatus === "new") {
        try {
          await registerCustomer({
            email: email.trim(),
            password,
            name: name.trim(),
            phone: phone.trim(),
          });
          setAuthed(true);
        } catch (regErr) {
          const d = regErr?.response?.data?.detail;
          toast.error(typeof d === "string" ? d : "Could not create account");
          setSubmitting(false);
          return;
        }
      }

      const res = await axios.post(`${API}/appointments`, payload, {
        headers: customerAuthHeaders(),
      });
      const appt = res.data;

      setConfirmed(appt);
      toast.success("Booking confirmed! We'll see you soon.");
      // Refresh slots since this one is now booked
      if (date) {
        const dStr = formatDateLocal(date);
        axios
          .get(`${API}/availability`, { params: { date_str: dStr, num_bins: bins } })
          .then((res2) => setSlots(res2.data?.slots || []));
      }
      // Reset form
      setSelectedTime(null);
      setName("");
      setEmail("");
      setPhone("");
      setAddress("");
      setNotes("");
      setPromoCode("");
      setAppliedPromo(null);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error(err);
      const msg = err?.response?.data?.detail || "Booking failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid={BOOKING.section}
      className="relative py-16 sm:py-20 border-b-4 border-[#0B132B] bg-[#FDFBF7]"
    >
      <div className="absolute inset-0 halftone opacity-40 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="inline-block tape px-4 py-1 rotate-n2 mb-4">
            <span className="font-['Bebas_Neue'] text-lg text-[#0B132B]">
              Book a Cleaning
            </span>
          </div>
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl">
            Pick a Day. Pick a Time. <span className="text-[#00E676]">Done.</span>
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-[#0B132B]/80">
            Pick any day and time that works for you — we'll handle the rest.
          </p>
        </div>

        {confirmed ? (
          <ConfirmedCard appt={confirmed} onBookAgain={() => setConfirmed(null)} />
        ) : (
          <form onSubmit={onSubmit} className="grid lg:grid-cols-12 gap-6">
            {/* Left: Step 1 + 2 */}
            <div className="lg:col-span-7 space-y-6">
              <div className="comic-card-static p-5 sm:p-6">
                <StepHeader number="1" label="How many bins?" />
                <div className="mt-4 flex flex-wrap items-center gap-6">
                  <BinStepper
                    value={bins}
                    setValue={setBins}
                    firstBinPrice={firstBinPrice}
                    extraBinPrice={extraBinPrice}
                  />
                </div>

                {/* Promo code */}
                <div className="mt-5 pt-4 border-t-2 border-dashed border-[#0B132B]/20">
                  <label className="block text-sm font-semibold mb-1">
                    Have a promo code?
                  </label>
                  {!appliedPromo ? (
                    <div className="flex gap-2 flex-wrap">
                      <input
                        data-testid="booking-promo-input"
                        type="text"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          if (promoError) setPromoError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            applyPromoCode();
                          }
                        }}
                        placeholder="Enter code"
                        className="comic-input flex-1 min-w-[180px] uppercase tracking-wider"
                        maxLength={40}
                      />
                      <button
                        type="button"
                        onClick={applyPromoCode}
                        disabled={!promoCode.trim() || promoBusy}
                        data-testid="booking-promo-apply"
                        className="comic-btn comic-btn-outline"
                        style={{ padding: "0.55rem 1rem" }}
                      >
                        {promoBusy ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Apply"
                        )}
                      </button>
                    </div>
                  ) : (
                    <div
                      data-testid="booking-promo-applied"
                      className="flex items-center justify-between gap-3 bg-[#00E676] border-[3px] border-[#0B132B] rounded-lg px-3 py-2 shadow-[3px_3px_0_#0B132B]"
                    >
                      <div className="min-w-0">
                        <div className="font-bold text-[#0B132B] text-sm">
                          <CheckCircle2 className="w-4 h-4 inline -mt-0.5" />{" "}
                          {appliedPromo.code} applied
                        </div>
                        {appliedPromo.description && (
                          <div className="text-xs text-[#0B132B]/80 truncate">
                            {appliedPromo.description}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={removePromo}
                        className="text-xs font-bold underline text-[#0B132B] shrink-0"
                        data-testid="booking-promo-remove"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {promoError && !appliedPromo && (
                    <div
                      data-testid="booking-promo-error"
                      className="text-xs text-[#FF3D00] font-semibold mt-1"
                    >
                      {promoError}
                    </div>
                  )}
                </div>
              </div>

              <div className="comic-card-static p-5 sm:p-6">
                <StepHeader number="2" label="Pick a date" />
                <div className="mt-4 flex justify-center">
                  <div data-testid={BOOKING.calendar} className="bg-white p-2 rounded-xl border-[3px] border-[#0B132B] shadow-[4px_4px_0_#0B132B]">
                    <DayPicker
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      weekStartsOn={0}
                      disabled={calendarDisabled}
                      fromDate={calendarFromDate}
                    />
                  </div>
                </div>
                <p className="mt-3 text-sm text-[#0B132B]/70 text-center">
                  Book any day — Dan will confirm with you.
                </p>
              </div>

              <div className="comic-card-static p-5 sm:p-6">
                <StepHeader number="3" label="Pick a time" />
                {!date && (
                  <p className="mt-4 text-[#0B132B]/70">Choose a date to see times.</p>
                )}
                {date && loadingSlots && (
                  <div className="mt-4 flex items-center gap-2 text-[#0B132B]/80">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading available times…
                  </div>
                )}
                {date && !loadingSlots && slots.length === 0 && (
                  <p className="mt-4 text-[#0B132B]/80">
                    No times available on this day. Try another date.
                  </p>
                )}
                {date && !loadingSlots && slots.length > 0 && (
                  <div
                    data-testid={BOOKING.slotsGrid}
                    className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5"
                  >
                    {slots.map((s) => {
                      const isSelected = selectedTime === s.time;
                      return (
                        <button
                          type="button"
                          key={s.time}
                          data-testid={BOOKING.slotBtn(s.time)}
                          disabled={!s.available}
                          data-selected={isSelected ? "true" : "false"}
                          className={isSelected ? "slot slot-selected" : "slot"}
                          onPointerUp={() => {
                            if (s.available) setSelectedTime(s.time);
                          }}
                          onClick={() => {
                            if (s.available) setSelectedTime(s.time);
                          }}
                        >
                          {formatTime12h(s.time)}
                        </button>
                      );
                    })}
                  </div>
                )}
                {date && !loadingSlots && slots.length > 0 && (
                  <div
                    data-testid="arrival-time-notice"
                    className="mt-4 flex items-start gap-2 p-3 bg-[#FFF4C2] border-[3px] border-[#0B132B] rounded-lg shadow-[3px_3px_0_#0B132B]"
                  >
                    <Clock className="w-5 h-5 mt-0.5 shrink-0 text-[#0B132B]" />
                    <p className="text-sm text-[#0B132B] leading-snug">
                      <span className="font-bold">Heads up:</span> your selected time is
                      a target window — actual arrival may shift slightly depending on
                      the day's schedule. Please allow a little extra time for travel
                      between stops. Dan will text/email if anything changes.
                    </p>
                  </div>
                )}
              </div>
              <div className="comic-card-static p-5 sm:p-6">
                <StepHeader number="4" label="Repeat?" />
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {[
                    { k: "none", label: "One Time" },
                    { k: "weekly", label: "Weekly" },
                    { k: "biweekly", label: "Bi-Weekly" },
                    { k: "monthly", label: "Monthly" },
                  ].map((r) => (
                    <button
                      key={r.k}
                      type="button"
                      data-testid={BOOKING.recurrenceBtn(r.k)}
                      onClick={() => setRecurrence(r.k)}
                      className="slot"
                      data-selected={recurrence === r.k ? "true" : "false"}
                      style={{ padding: "0.75rem 0.5rem" }}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                {recurrence !== "none" && (
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold">How many visits?</label>
                    <input
                      data-testid={BOOKING.occurrencesInput}
                      type="number"
                      min={2}
                      max={12}
                      value={occurrences}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!isNaN(v)) setOccurrences(Math.min(12, Math.max(2, v)));
                      }}
                      className="comic-input"
                      style={{ width: "5rem", textAlign: "center" }}
                    />
                    <div className="text-sm text-[#0B132B]/80">
                      Total: ${perVisit.toFixed(2)} × {occurrences} ={" "}
                      <span className="font-bold text-[#1E88E5]">${(perVisit * occurrences).toFixed(2)}</span>
                    </div>
                  </div>
                )}
                {recurrence !== "none" && (
                  <p className="mt-3 text-xs text-[#0B132B]/70">
                    Same day & time will be reserved for each visit. If any future date conflicts, you'll be asked to pick another start time.
                  </p>
                )}
              </div>
            </div>

            {/* Right: Step 5 + checkout */}
            <div className="lg:col-span-5 space-y-6">
              <div className="comic-card-static p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <StepHeader number="5" label="Your info" />
                  {authed && me ? (
                    <div className="inline-flex items-center gap-2 text-xs font-semibold bg-[#00E676] border-[2px] border-[#0B132B] rounded-md px-2 py-1 shadow-[2px_2px_0_#0B132B]">
                      Signed in as {me.name?.split(" ")[0]}
                      <button
                        type="button"
                        onClick={() => {
                          logoutCustomer();
                          setAuthed(false);
                          setMe(null);
                          setEmailStatus("idle");
                        }}
                        className="underline ml-1"
                      >
                        sign out
                      </button>
                    </div>
                  ) : (
                    <a href="/account" className="text-xs underline text-[#0B132B]/70">
                      Already have an account? Log in
                    </a>
                  )}
                </div>
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-semibold mb-1">Full name</label>
                    <input
                      data-testid={BOOKING.nameInput}
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Jane Smith"
                      className="comic-input"
                      required
                      disabled={authed}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold mb-1">Email</label>
                      <input
                        data-testid={BOOKING.emailInput}
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          // Clear stale status so the password callout doesn't
                          // stay mounted while they're correcting a typo.
                          if (emailStatus !== "idle") setEmailStatus("idle");
                        }}
                        onBlur={() => runEmailLookup(email)}
                        placeholder="you@example.com"
                        className="comic-input"
                        required
                        disabled={authed}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-1">Phone</label>
                      <input
                        data-testid={BOOKING.phoneInput}
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="(860) 555-1234"
                        className="comic-input"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Service address</label>
                    <input
                      data-testid={BOOKING.addressInput}
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="123 Main St"
                      className="comic-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">City</label>
                    <select
                      data-testid={BOOKING.citySelect}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="comic-input"
                    >
                      {CITIES.map((c) => (
                        <option key={c} value={c}>
                          {c}, CT
                        </option>
                      ))}
                      <option value={OUT_OF_AREA}>
                        Somewhere else? (approval required)
                      </option>
                    </select>
                    {isOutOfArea && (
                      <>
                        <input
                          data-testid="booking-out-of-area-city"
                          type="text"
                          value={outOfAreaCity}
                          onChange={(e) => setOutOfAreaCity(e.target.value)}
                          placeholder="Your town (e.g. Cromwell, CT)"
                          className="comic-input mt-2"
                          required
                        />
                        <div
                          data-testid="out-of-area-warning"
                          className="mt-3 bg-[#FFC107] border-[3px] border-[#0B132B] rounded-lg p-3 shadow-[3px_3px_0_#0B132B] text-sm text-[#0B132B]"
                        >
                          <div className="font-bold mb-1">
                            Heads up — outside our service area
                          </div>
                          <p className="text-xs leading-snug">
                            We'll forward your request to Dan for approval based on
                            travel availability. If approved, a{" "}
                            <span className="font-bold">$5 travel surcharge</span>{" "}
                            will be added to your total. We'll be in touch within 24
                            hours to confirm.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-1">Notes (optional)</label>
                    <textarea
                      data-testid={BOOKING.notesInput}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      placeholder="Gate code, special instructions…"
                      className="comic-textarea"
                    />
                  </div>
                </div>
              </div>

              <div className="comic-card-static p-5 sm:p-6">
                <StepHeader number="6" label="Payment" />
                <div className="mt-4 inline-flex items-center gap-2 bg-[#00E676] border-[3px] border-[#0B132B] rounded-lg px-3 py-2 shadow-[3px_3px_0_#0B132B]">
                  <span className="font-semibold text-sm">
                    We accept Cash, Check, or Venmo
                  </span>
                </div>
                <p className="mt-3 text-sm text-[#0B132B]/70">
                  No charge today — just have ${totalAmount} ready at the time of service.
                </p>

                <div className="mt-5 flex items-center justify-between border-t-2 border-dashed border-[#0B132B]/30 pt-4">
                  <div className="font-['Bebas_Neue'] text-xl">Total Due</div>
                  <div
                    data-testid={BOOKING.totalAmount}
                    className="font-['Bebas_Neue'] text-3xl text-[#1E88E5]"
                  >
                    ${totalAmount}
                  </div>
                </div>
                {isOutOfArea && (
                  <div
                    data-testid="surcharge-line"
                    className="mt-2 text-xs text-[#0B132B]/80 text-right"
                  >
                    Includes <span className="font-bold">$5 travel surcharge</span> · pending approval
                  </div>
                )}

                {!authed && emailStatus === "new" && (
                  <div
                    data-testid="booking-new-account-callout"
                    className="mt-5 bg-[#FFC107] border-[3px] border-[#0B132B] rounded-lg p-4 shadow-[4px_4px_0_#0B132B]"
                  >
                    <div className="flex items-start gap-2 mb-3">
                      <Sparkles className="w-5 h-5 mt-0.5 shrink-0 text-[#0B132B]" />
                      <div>
                        <div className="font-['Bebas_Neue'] text-xl tracking-wide leading-none">
                          Create Your Account
                        </div>
                        <div className="text-xs text-[#0B132B]/80 mt-1">
                          One step left — set a password so you can come back to
                          reschedule, cancel, or rebook.
                        </div>
                      </div>
                    </div>
                    <label className="block text-sm font-bold mb-1 text-[#0B132B]">
                      Password <span className="text-[#FF3D00]">*</span>
                    </label>
                    <div className="relative">
                      <input
                        data-testid="booking-new-password"
                        type={showRegPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="At least 6 characters"
                        className="comic-input"
                        style={{ paddingRight: "2.75rem", background: "#FFFFFF" }}
                        minLength={6}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegPassword((v) => !v)}
                        data-testid="booking-new-password-toggle"
                        aria-label={showRegPassword ? "Hide password" : "Show password"}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[#FFF4C2] text-[#0B132B]"
                      >
                        {showRegPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    {password && password.length < 6 && (
                      <div className="text-xs text-[#FF3D00] font-semibold mt-1">
                        Password must be at least 6 characters
                      </div>
                    )}
                  </div>
                )}
                {!authed && emailStatus === "existing" && (
                  <div
                    data-testid="booking-existing-account-callout"
                    className="mt-5 bg-[#1E88E5] text-white border-[3px] border-[#0B132B] rounded-lg p-4 shadow-[4px_4px_0_#0B132B]"
                  >
                    <div className="flex items-start gap-2 mb-3">
                      <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0 text-white" />
                      <div>
                        <div className="font-['Bebas_Neue'] text-xl tracking-wide leading-none">
                          Welcome Back!
                        </div>
                        <div className="text-xs text-white/90 mt-1">
                          We found your account. Log in with your password to
                          continue booking.
                        </div>
                      </div>
                    </div>
                    <label className="block text-sm font-bold mb-1 text-white">
                      Password <span className="text-[#FFC107]">*</span>
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <input
                          data-testid="booking-login-password"
                          type={showLoginPassword ? "text" : "password"}
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          placeholder="Your password"
                          className="comic-input w-full"
                          style={{ paddingRight: "2.75rem", background: "#FFFFFF", color: "#0B132B" }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (loginPassword && !authBusy) doInlineLogin();
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword((v) => !v)}
                          data-testid="booking-login-password-toggle"
                          aria-label={showLoginPassword ? "Hide password" : "Show password"}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[#FFF4C2] text-[#0B132B]"
                        >
                          {showLoginPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={doInlineLogin}
                        disabled={authBusy || !loginPassword}
                        className="comic-btn comic-btn-action"
                        style={{ padding: "0.55rem 1.2rem", fontSize: "0.95rem" }}
                        data-testid="booking-inline-login"
                      >
                        {authBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Log in
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  data-testid={BOOKING.submitBtn}
                  disabled={!canSubmit}
                  className="mt-4 w-full comic-btn comic-btn-action"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" /> Booking…
                    </>
                  ) : (
                    <>
                      <CalendarCheck className="w-5 h-5" /> Confirm Booking
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function StepHeader({ number, label }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-[#FF3D00] text-white border-[3px] border-[#0B132B] grid place-items-center font-['Bebas_Neue'] text-xl shadow-[3px_3px_0_#0B132B]">
        {number}
      </div>
      <div className="font-['Bebas_Neue'] text-2xl sm:text-3xl tracking-wide">{label}</div>
    </div>
  );
}

function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [sliderPct, setSliderPct] = useState(50);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/gallery`)
      .then((r) => mounted && setItems(r.data || []))
      .catch(() => mounted && setItems([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Reset slider when active item changes
  useEffect(() => {
    setSliderPct(50);
  }, [activeIdx]);

  // Auto-advance slideshow every 6s
  useEffect(() => {
    if (paused || items.length < 2) return;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % items.length);
    }, 6000);
    return () => clearInterval(id);
  }, [paused, items.length]);

  if (loading) return null;
  if (!items.length) return null;

  const active = items[activeIdx] || items[0];
  const hasAfter = Boolean(active.after_image);
  const goPrev = () =>
    setActiveIdx((i) => (i - 1 + items.length) % items.length);
  const goNext = () => setActiveIdx((i) => (i + 1) % items.length);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-16 sm:py-20 border-b-4 border-[#0B132B] bg-[#FDFBF7]"
    >
      <div className="absolute inset-0 halftone opacity-40 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <div className="inline-block tape px-4 py-1 -rotate-1 mb-4 bg-white">
            <span className="font-['Bebas_Neue'] text-lg text-[#0B132B]">
              Before / After
            </span>
          </div>
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl text-[#0B132B]">
            See The <span className="text-[#00E676]">Dirty Deeds</span>{" "}
            <span className="text-[#0B132B]">Done.</span>
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-[#0B132B]/80">
            Real bins. Real grime. Real results.
          </p>
        </div>

        {/* Slideshow viewer */}
        <div
          data-testid="gallery-slider"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          className="comic-card-static bg-white p-3 sm:p-4 max-w-3xl mx-auto relative"
        >
          <div
            className="relative w-full aspect-[16/10] overflow-hidden rounded-lg border-[3px] border-[#0B132B] select-none"
            style={{ background: "#0B132B" }}
          >
            {hasAfter ? (
              <>
                {/* After image as base layer */}
                <img
                  src={active.after_image}
                  alt={`${active.title} — after`}
                  className="absolute inset-0 w-full h-full object-contain"
                  style={{ background: "#0B132B" }}
                  draggable="false"
                />
                {/* Before image clipped via CSS clip-path (keeps full aspect ratio) */}
                <img
                  src={active.before_image}
                  alt={`${active.title} — before`}
                  className="absolute inset-0 w-full h-full object-contain"
                  style={{
                    background: "#0B132B",
                    clipPath: `inset(0 ${100 - sliderPct}% 0 0)`,
                  }}
                  draggable="false"
                />
                <div className="absolute top-3 left-3 bg-[#FF3D00] text-white text-xs font-bold px-2 py-1 rounded border-[2px] border-[#0B132B]">
                  BEFORE
                </div>
                <div className="absolute top-3 right-3 bg-[#00E676] text-[#0B132B] text-xs font-bold px-2 py-1 rounded border-[2px] border-[#0B132B]">
                  AFTER
                </div>
                <div
                  className="absolute top-0 bottom-0 w-[3px] bg-[#0B132B] pointer-events-none"
                  style={{ left: `calc(${sliderPct}% - 1.5px)` }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border-[3px] border-[#0B132B] grid place-items-center pointer-events-none shadow-[2px_2px_0_#0B132B]"
                  style={{ left: `calc(${sliderPct}% - 18px)` }}
                >
                  <ChevronLeft className="w-3 h-3 -mr-1" />
                  <ChevronRight className="w-3 h-3 -ml-1" />
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={sliderPct}
                  onChange={(e) => setSliderPct(Number(e.target.value))}
                  data-testid="gallery-slider-input"
                  aria-label="Drag to compare before and after"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
                />
              </>
            ) : (
              <img
                src={active.before_image}
                alt={active.title}
                className="absolute inset-0 w-full h-full object-contain"
                style={{ background: "#0B132B" }}
                draggable="false"
              />
            )}
          </div>

          {/* Prev / Next chevrons */}
          {items.length > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous"
                data-testid="gallery-prev"
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-3 w-10 h-10 rounded-full bg-white border-[3px] border-[#0B132B] grid place-items-center shadow-[3px_3px_0_#0B132B] hover:bg-[#FFC107] transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next"
                data-testid="gallery-next"
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-3 w-10 h-10 rounded-full bg-white border-[3px] border-[#0B132B] grid place-items-center shadow-[3px_3px_0_#0B132B] hover:bg-[#FFC107] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}

          <div className="mt-3 px-1 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-['Bebas_Neue'] text-2xl leading-tight truncate">
                {active.title}
              </div>
              {active.caption && (
                <p className="text-sm text-[#0B132B]/70 mt-1">{active.caption}</p>
              )}
            </div>
            {items.length > 1 && (
              <div
                data-testid="gallery-counter"
                className="text-xs font-bold bg-[#0B132B] text-white px-2 py-1 rounded shrink-0"
              >
                {activeIdx + 1} / {items.length}
              </div>
            )}
          </div>

          {/* Dots */}
          {items.length > 1 && (
            <div className="mt-3 flex justify-center gap-2" data-testid="gallery-dots">
              {items.map((it, i) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`gallery-dot-${i}`}
                  className="w-2.5 h-2.5 rounded-full border-[2px] border-[#0B132B] transition-colors"
                  style={{
                    background: i === activeIdx ? "#FF3D00" : "#FFFFFF",
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function StarPicker({ value, onChange, size = "w-7 h-7", testIdPrefix = "star" }) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="inline-flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`${testIdPrefix}-${n}`}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          className="transition-transform hover:scale-110 focus:outline-none"
        >
          <Star
            className={size}
            style={{
              fill: n <= active ? "#FFC107" : "transparent",
              stroke: "#0B132B",
              strokeWidth: 2.5,
            }}
          />
        </button>
      ))}
    </div>
  );
}

function StarsDisplay({ rating, size = "w-5 h-5" }) {
  return (
    <div className="inline-flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={size}
          style={{
            fill: n <= rating ? "#FFC107" : "transparent",
            stroke: "#0B132B",
            strokeWidth: 2.5,
          }}
        />
      ))}
    </div>
  );
}

function Reviews() {
  // Slideshow state
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [city, setCity] = useState(CITIES[0]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/reviews`)
      .then((r) => mounted && setItems(r.data || []))
      .catch(() => mounted && setItems([]));
    return () => {
      mounted = false;
    };
  }, []);

  // Auto-advance every 6s
  useEffect(() => {
    if (paused || items.length < 2) return;
    const id = setInterval(() => {
      setActiveIdx((i) => (i + 1) % items.length);
    }, 6000);
    return () => clearInterval(id);
  }, [paused, items.length]);

  // Pre-fill if logged in
  useEffect(() => {
    if (!showForm || !getCustomerToken()) return;
    fetchMe()
      .then((m) => {
        if (!name && m.name) setName(m.name);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);

  const canSubmit =
    name.trim().length >= 2 &&
    comment.trim().length >= 5 &&
    rating >= 1 &&
    rating <= 5 &&
    !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please add your name and a short comment.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        customer_name: name.trim(),
        rating,
        comment: comment.trim(),
        city: city || null,
      });
      setSubmitted(true);
      toast.success("Thanks for the review!");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const goPrev = () =>
    setActiveIdx((i) => (i - 1 + items.length) % items.length);
  const goNext = () => setActiveIdx((i) => (i + 1) % items.length);

  const active = items[activeIdx];

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-16 sm:py-20 border-b-4 border-[#0B132B] bg-[#FDFBF7]"
    >
      <div className="absolute inset-0 halftone opacity-30 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="inline-block tape px-4 py-1 -rotate-1 mb-4 bg-white">
            <span className="font-['Bebas_Neue'] text-lg text-[#0B132B]">
              What Neighbors Say
            </span>
          </div>
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl text-[#0B132B]">
            Real Reviews From{" "}
            <span className="text-[#FF3D00]">Real People</span>
          </h2>
        </div>

        {/* Slideshow */}
        {items.length > 0 && active && (
          <div
            data-testid="reviews-slideshow"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className="comic-card-static bg-white p-6 sm:p-8 max-w-3xl mx-auto relative"
          >
            <div className="text-center min-h-[180px]">
              <StarsDisplay rating={active.rating} size="w-6 h-6" />
              <blockquote className="mt-4 text-lg sm:text-xl italic text-[#0B132B] leading-relaxed">
                "{active.comment}"
              </blockquote>
              <div className="mt-5 font-['Bebas_Neue'] text-2xl tracking-wide text-[#0B132B]">
                — {active.customer_name}
              </div>
              {active.city && (
                <div className="text-sm text-[#0B132B]/70 mt-1">
                  {active.city}, CT
                </div>
              )}
            </div>

            {items.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous review"
                  data-testid="reviews-prev"
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 w-10 h-10 rounded-full bg-white border-[3px] border-[#0B132B] grid place-items-center shadow-[3px_3px_0_#0B132B] hover:bg-[#FFC107] transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next review"
                  data-testid="reviews-next"
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 w-10 h-10 rounded-full bg-white border-[3px] border-[#0B132B] grid place-items-center shadow-[3px_3px_0_#0B132B] hover:bg-[#FFC107] transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                <div
                  className="mt-6 flex justify-center gap-2"
                  data-testid="reviews-dots"
                >
                  {items.map((it, i) => (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => setActiveIdx(i)}
                      aria-label={`Review ${i + 1}`}
                      data-testid={`reviews-dot-${i}`}
                      className="w-2.5 h-2.5 rounded-full border-[2px] border-[#0B132B] transition-colors"
                      style={{
                        background: i === activeIdx ? "#FF3D00" : "#FFFFFF",
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Leave a review CTA / form */}
        <div className="mt-10 max-w-2xl mx-auto">
          {submitted ? (
            <div
              data-testid="review-confirmed"
              className="comic-card-static bg-white p-6 text-center"
            >
              <div className="inline-flex w-14 h-14 rounded-full bg-[#00E676] border-[3px] border-[#0B132B] items-center justify-center mb-3 shadow-[4px_4px_0_#0B132B]">
                <CheckCircle2 className="w-7 h-7 text-[#0B132B]" />
              </div>
              <h3 className="font-['Bebas_Neue'] text-2xl">Thanks for the review!</h3>
              <p className="mt-1 text-sm text-[#0B132B]/80">
                We really appreciate you taking the time.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  setShowForm(false);
                  setName("");
                  setComment("");
                  setRating(5);
                }}
                className="mt-4 comic-btn comic-btn-outline"
                style={{ padding: "0.5rem 1rem" }}
                data-testid="review-done-btn"
              >
                Done
              </button>
            </div>
          ) : !showForm ? (
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForm(true)}
                data-testid="review-open-btn"
                className="comic-btn comic-btn-action text-lg"
                style={{ padding: "0.75rem 1.5rem" }}
              >
                <Star className="w-5 h-5" /> Leave a Review
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              data-testid="review-form"
              className="comic-card-static bg-white p-5 sm:p-7 space-y-4"
            >
              <div className="flex items-center justify-between">
                <div className="font-['Bebas_Neue'] text-2xl tracking-wide">
                  Share Your Experience
                </div>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  data-testid="review-close-btn"
                  className="text-xs font-semibold underline text-[#0B132B]/70 hover:text-[#FF3D00]"
                >
                  Cancel
                </button>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  How many stars?
                </label>
                <StarPicker
                  value={rating}
                  onChange={setRating}
                  testIdPrefix="review-star"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Your name *
                  </label>
                  <input
                    data-testid="review-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Smith"
                    className="comic-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">City</label>
                  <select
                    data-testid="review-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="comic-input"
                  >
                    {CITIES.map((c) => (
                      <option key={c} value={c}>
                        {c}, CT
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">
                  Your review *
                </label>
                <textarea
                  data-testid="review-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  placeholder="Tell other neighbors how it went…"
                  className="comic-textarea"
                  required
                  maxLength={1000}
                />
                <div className="text-[11px] text-[#0B132B]/60 mt-1 text-right">
                  {comment.length}/1000
                </div>
              </div>

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="review-submit"
                className="w-full comic-btn comic-btn-action"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                  </>
                ) : (
                  <>Submit Review</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const OTHER_SERVICE_OPTIONS = [
  { k: "landscaping", label: "Landscaping / Yard Work" },
  { k: "weed_pulling", label: "Weed Pulling" },
  { k: "power_washing", label: "Power Washing" },
  { k: "leaf_raking", label: "Leaf Raking" },
  { k: "snow_shoveling", label: "Snow Shoveling" },
  { k: "other", label: "Something Else" },
];

function OtherServices() {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState(CITIES[0]);
  const [preferredDate, setPreferredDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pre-fill from logged-in customer when expanded
  useEffect(() => {
    if (!open || !getCustomerToken()) return;
    fetchMe()
      .then((m) => {
        if (!name) setName(m.name || "");
        if (!email) setEmail(m.email || "");
        if (!phone && m.phone) setPhone(m.phone);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSubmit =
    name.trim().length >= 2 &&
    /^\S+@\S+\.\S+$/.test(email) &&
    phone.replace(/\D/g, "").length >= 7 &&
    description.trim().length >= 5 &&
    !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Please fill out the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/service-requests`, {
        customer_name: name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim(),
        address: address.trim() || null,
        city,
        service_type: "other",
        description: description.trim(),
        preferred_date: preferredDate.trim() || null,
      });
      setSubmitted(true);
      toast.success("Request sent! Dan will reach out soon.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="other-services"
      data-testid="other-services-section"
      className="relative py-16 sm:py-20 border-b-4 border-[#0B132B] bg-[#FFC107]"
    >
      <div className="absolute inset-0 halftone opacity-30 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <div className="inline-block tape px-4 py-1 rotate-p2 mb-4 bg-white">
            <span className="font-['Bebas_Neue'] text-lg text-[#0B132B]">
              Need Something Else?
            </span>
          </div>
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl text-[#0B132B]">
            Got A One-Off Job? <span className="text-[#FF3D00]">Ask Dan.</span>
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-[#0B132B]/85">
            Have another small request or task? Submit it below.
          </p>
        </div>

        {submitted ? (
          <div
            data-testid="other-services-confirmed"
            className="comic-card-static bg-white p-6 sm:p-8 max-w-xl mx-auto text-center"
          >
            <div className="inline-flex w-14 h-14 rounded-full bg-[#00E676] border-[3px] border-[#0B132B] items-center justify-center mb-4 shadow-[4px_4px_0_#0B132B]">
              <CheckCircle2 className="w-7 h-7 text-[#0B132B]" />
            </div>
            <h3 className="font-['Bebas_Neue'] text-3xl">Request Sent!</h3>
            <p className="mt-2 text-[#0B132B]/80">
              Thanks, {name.split(" ")[0]}. Dan will be in touch soon with a quote.
            </p>
            <button
              type="button"
              onClick={() => {
                setSubmitted(false);
                setDescription("");
                setPreferredDate("");
                setOpen(true);
              }}
              className="mt-5 comic-btn comic-btn-primary"
              data-testid="other-services-new-btn"
            >
              Send another request
            </button>
          </div>
        ) : !open ? (
          <div className="text-center">
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="other-services-open-btn"
              className="comic-btn comic-btn-action text-lg sm:text-xl"
              style={{ padding: "0.85rem 1.8rem" }}
            >
              <MessageSquare className="w-5 h-5" /> Click to Request
              <ChevronDown className="w-5 h-5" />
            </button>
            <p className="mt-3 text-xs text-[#0B132B]/70">
              Takes 30 seconds. No commitment.
            </p>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="comic-card-static bg-white p-5 sm:p-7 grid lg:grid-cols-2 gap-5"
            data-testid="other-services-form"
          >
            <div className="lg:col-span-2 flex justify-end -mt-2 -mr-1">
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="other-services-close-btn"
                className="text-xs font-semibold underline text-[#0B132B]/70 hover:text-[#FF3D00]"
              >
                Hide form
              </button>
            </div>
            <div className="lg:col-span-2">
              <label className="block text-sm font-semibold mb-1">
                What do you need done?
              </label>
              <textarea
                data-testid="os-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Tell Dan what you'd like done — yard work, power washing, anything. Include any details that help him quote it."
                className="comic-textarea"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Full name</label>
              <input
                data-testid="os-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                className="comic-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Phone</label>
              <input
                data-testid="os-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(860) 555-1234"
                className="comic-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Email</label>
              <input
                data-testid="os-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="comic-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Preferred date (optional)</label>
              <input
                data-testid="os-preferred-date"
                type="text"
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                placeholder="e.g. Saturday, next weekend, ASAP"
                className="comic-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Address (optional)</label>
              <input
                data-testid="os-address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St"
                className="comic-input"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">City</label>
              <select
                data-testid="os-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="comic-input"
              >
                {CITIES.map((c) => (
                  <option key={c} value={c}>
                    {c}, CT
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-2">
              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="os-submit"
                className="w-full comic-btn comic-btn-action"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-5 h-5" /> Send Request
                  </>
                )}
              </button>
              <p className="mt-2 text-xs text-[#0B132B]/70 text-center">
                No commitment — Dan will text or email you with a quote.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function ConfirmedCard({ appt, onBookAgain }) {
  const start = new Date(appt.start_at);
  const dateStr = start.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  const timeStr = start.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return (
    <div className="max-w-2xl mx-auto comic-card-static p-6 sm:p-8 text-center">
      <div className="inline-flex w-14 h-14 rounded-full bg-[#00E676] border-[3px] border-[#0B132B] items-center justify-center mb-4 shadow-[4px_4px_0_#0B132B]">
        <CheckCircle2 className="w-7 h-7 text-[#0B132B]" />
      </div>
      <h3 className="font-['Bebas_Neue'] text-4xl">You're Booked!</h3>
      <p className="mt-2 text-[#0B132B]/80">
        Thanks, {appt.customer_name.split(" ")[0]}. Here are your details:
      </p>
      <div className="mt-5 inline-block text-left bg-[#FFC107]/40 border-[3px] border-[#0B132B] rounded-xl px-5 py-4 shadow-[4px_4px_0_#0B132B]">
        <div className="text-sm">
          <span className="font-bold">When:</span> {dateStr} at {timeStr}
        </div>
        <div className="text-sm">
          <span className="font-bold">Bins:</span> {appt.num_bins}
        </div>
        <div className="text-sm">
          <span className="font-bold">Total:</span> ${appt.total_amount.toFixed(2)}{" "}
          (Cash, Check, or Venmo at service)
        </div>
        <div className="text-sm">
          <span className="font-bold">Confirmation #:</span>{" "}
          {appt.id.slice(0, 8).toUpperCase()}
        </div>
      </div>
      <p className="mt-4 text-sm text-[#0B132B]/70">
        Questions? Call or text Dan at{" "}
        <a className="font-bold underline" href={`tel:${PHONE_TEL}`}>
          {PHONE}
        </a>
        .
      </p>
      <button onClick={onBookAgain} className="mt-5 comic-btn comic-btn-primary">
        Book another cleaning
      </button>
    </div>
  );
}

function CallStrip() {
  return (
    <section className="strip py-6 border-b-4 border-[#0B132B]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="font-['Bebas_Neue'] text-2xl sm:text-3xl text-center sm:text-left">
          Prefer to talk?{" "}
          <span className="text-[#00E676]">Call or Text My Dad Dan</span>
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          <a
            data-testid={FOOTER.callLink}
            href={`tel:${PHONE_TEL}`}
            className="comic-btn comic-btn-primary"
          >
            <Phone className="w-5 h-5" /> Call {PHONE}
          </a>
          <a
            data-testid={FOOTER.textLink}
            href={`sms:${PHONE_SMS}`}
            className="comic-btn comic-btn-blue"
          >
            <MessageSquare className="w-5 h-5" /> Text {PHONE}
          </a>
        </div>
      </div>
    </section>
  );
}

function ContactPicker({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#FDFBF7] border-[4px] border-[#0B132B] rounded-2xl p-6 shadow-[8px_8px_0_#0B132B] max-w-sm"
        data-testid="contact-picker-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-['Bebas_Neue'] text-3xl tracking-wide text-center">
            Call or Text?
          </DialogTitle>
          <DialogDescription className="text-center text-[#0B132B]/70">
            Pick how you'd like to reach Dan at {PHONE}.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <a
            href={`tel:${PHONE_TEL}`}
            onClick={() => onOpenChange(false)}
            className="comic-btn comic-btn-primary"
            data-testid="contact-picker-call"
          >
            <Phone className="w-5 h-5" /> Call
          </a>
          <a
            href={`sms:${PHONE_SMS}`}
            onClick={() => onOpenChange(false)}
            className="comic-btn comic-btn-blue"
            data-testid="contact-picker-text"
          >
            <MessageSquare className="w-5 h-5" /> Text
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Footer() {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <footer
      data-testid={FOOTER.section}
      className="bg-[#0B132B] text-white py-10 border-t-4 border-[#0B132B]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-8">
        <div>
          <div className="font-['Bebas_Neue'] text-3xl">
            <span className="text-[#00E676]">DANIEL'S</span> DIRTY DEEDS
          </div>
          <p className="mt-2 text-white/80 text-sm leading-relaxed">
            Professional. Reliable. Affordable. You relax, we do the dirty deeds.
          </p>
        </div>
        <div>
          <div className="font-['Bebas_Neue'] text-2xl text-[#00E676]">Serving</div>
          <ul className="mt-2 space-y-1 text-white/90">
            {CITIES.map((c) => (
              <li key={c} className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#00E5FF]" /> {c}, CT
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="font-['Bebas_Neue'] text-2xl text-[#00E676]">Contact</div>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            data-testid={FOOTER.callLink}
            className="mt-2 inline-flex items-center gap-2 text-white/90 hover:text-[#00E676] underline underline-offset-2"
          >
            <Phone className="w-4 h-4" /> Call or Text {PHONE}
          </button>
        </div>
      </div>
      <div className="text-center text-white/60 text-xs mt-8">
        © {new Date().getFullYear()} Daniel's Dirty Deeds • Wethersfield, CT •{" "}
        <a href="/admin" className="underline hover:text-[#00E676]" data-testid="footer-admin-link">
          Admin
        </a>
      </div>
      <ContactPicker open={pickerOpen} onOpenChange={setPickerOpen} />
    </footer>
  );
}

function SeasonClosureNote() {
  const [closure, setClosure] = useState(null);
  const [dismissed, setDismissed] = useState(true); // stay hidden until we know

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/schedule/config`)
      .then((res) => {
        if (cancelled) return;
        const c = res.data?.season_closure;
        if (!c || !c.active) {
          setClosure(null);
          setDismissed(true);
          return;
        }
        setClosure(c);
        const key = `dddSeasonNote:${c.reopen_date || "active"}`;
        const wasDismissed =
          typeof window !== "undefined" && localStorage.getItem(key) === "1";
        setDismissed(wasDismissed);
      })
      .catch(() => setClosure(null));
    return () => {
      cancelled = true;
    };
  }, []);

  if (!closure || dismissed) return null;

  const reopenLabel = (() => {
    if (!closure.reopen_date) return "next season";
    try {
      const [y, m, d] = closure.reopen_date.split("-").map((v) => parseInt(v, 10));
      const dt = new Date(y, m - 1, d);
      return dt.toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return closure.reopen_date;
    }
  })();

  const handleDismiss = () => {
    try {
      const key = `dddSeasonNote:${closure.reopen_date || "active"}`;
      localStorage.setItem(key, "1");
    } catch {}
    setDismissed(true);
  };

  return (
    <div
      data-testid="season-closure-note"
      role="dialog"
      aria-label="Seasonal closure notice"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0B132B]/55 backdrop-blur-sm"
    >
      <div
        className="relative w-full max-w-md"
        style={{ transform: "rotate(-2.5deg)" }}
      >
        <div
          aria-hidden="true"
          className="absolute left-1/2 -top-4 -translate-x-1/2 w-24 h-6"
          style={{
            background: "#FEF3C7",
            border: "2px solid rgba(11,19,43,0.4)",
            transform: "translateX(-50%) rotate(-4deg)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
            opacity: 0.9,
          }}
        />
        <div
          className="relative p-6 sm:p-7"
          style={{
            background: "linear-gradient(180deg, #FFF59D 0%, #FFEE58 100%)",
            border: "3px solid #0B132B",
            borderRadius: 6,
            boxShadow:
              "8px 8px 0 rgba(11,19,43,0.85), 0 20px 40px rgba(0,0,0,0.25)",
            color: "#0B132B",
          }}
        >
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="season-closure-close-btn"
            aria-label="Close notice"
            className="absolute top-2 right-2 w-9 h-9 rounded-full grid place-items-center hover:bg-[#0B132B] hover:text-white transition-colors"
            style={{
              background: "#FFFFFF",
              border: "2.5px solid #0B132B",
              boxShadow: "2px 2px 0 #0B132B",
            }}
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>

          <div className="text-center">
            <div
              className="inline-block px-3 py-1 mb-3 text-xs sm:text-sm font-bold tracking-widest uppercase"
              style={{
                background: "#0B132B",
                color: "#FFEE58",
                borderRadius: 4,
                transform: "rotate(1.5deg)",
              }}
            >
              A quick note from Daniel
            </div>
            <h2
              className="text-3xl sm:text-4xl leading-tight mb-3"
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                letterSpacing: "0.03em",
              }}
            >
              Closing Down for the Season
            </h2>
            <p className="text-base sm:text-lg leading-snug mb-2">
              Time for me to hang up the hose and focus on my student-athlete
              commitments!
            </p>
            <p className="text-base sm:text-lg leading-snug">
              I'm already excited to open back up on{" "}
              <span
                className="font-bold px-1"
                style={{ background: "#0B132B", color: "#FFEE58" }}
              >
                {reopenLabel}
              </span>
              .
            </p>
            <p className="text-sm sm:text-base mt-4 leading-snug">
              The calendar is already open for appointments starting then —
              close this note and grab your spot for next year.
            </p>
            <div className="mt-5">
              <button
                type="button"
                onClick={handleDismiss}
                data-testid="season-closure-book-next-year"
                className="comic-btn comic-btn-action"
                style={{ padding: "0.6rem 1.2rem" }}
              >
                Book for next year
              </button>
            </div>
            <div className="mt-4 text-sm opacity-80">— Daniel</div>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function Home() {
  useEffect(() => {
    document.title = "Daniel's Dirty Deeds — Trash Bin Cleaning Service";
  }, []);
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SeasonClosureNote />
      <NavBar />
      <Hero />
      <Features />
      <AboutDaniel />
      <Reviews />
      <BookingSection />
      {/* <OtherServices /> — disabled per request, keep code for easy re-enable */}
      <Footer />
    </div>
  );
}
